<template>
  <div class="filter-menu" ref="root">
    <button
      type="button"
      class="filter-btn"
      :class="{ active: selectedCount > 0 }"
      :aria-expanded="open"
      aria-haspopup="true"
      @click="open = !open"
      @keydown.esc="open = false"
    >
      {{ label }}
      <span v-if="selectedCount" class="badge">{{ selectedCount }}</span>
      <svg class="caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
        <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    </button>

    <div v-if="open" class="panel" role="group" :aria-label="`${label} filter`" @keydown.esc="close">
      <label
        v-for="option in options"
        :key="option.value"
        class="option"
        :class="{ dim: !option.count && !option.active }"
      >
        <input type="checkbox" :checked="option.active" @change="emit('toggle', option.value)">
        <span class="option-name">{{ option.value }}</span>
        <span class="option-count">{{ option.count }}</span>
      </label>
      <p v-if="!options.length" class="empty">Nothing to filter by yet.</p>
      <button v-if="selectedCount" type="button" class="reset" @click="emit('clear')">
        Clear {{ label.toLowerCase() }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] }, // [{ value, count, active }]
  selectedCount: { type: Number, default: 0 },
});
const emit = defineEmits(['toggle', 'clear']);

const open = ref(false);
const root = ref(null);
const close = () => { open.value = false; };

// Click anywhere else closes the panel
const onDocumentClick = event => {
  if (open.value && root.value && !root.value.contains(event.target)) close();
};
onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>

<style scoped>
.filter-menu {
  position: relative;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--color-surface-light);
  color: var(--color-text);
  border: 1px solid transparent;
  border-radius: var(--border-radius-pill);
  padding: 0.4rem 0.9rem;
  font-size: var(--font-size-sm);
  transition: background-color var(--transition-base);
}

.filter-btn:hover,
.filter-btn[aria-expanded='true'] {
  background: var(--color-surface-lighter);
}

.filter-btn.active {
  border-color: var(--color-primary);
}

.filter-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.badge {
  background: var(--color-primary);
  color: #000;
  border-radius: 999px;
  min-width: 1.2rem;
  padding: 0 0.35rem;
  font-size: var(--font-size-xs);
  font-weight: 700;
  text-align: center;
}

.caret {
  opacity: 0.7;
}

@media (max-width: 479px) {
  .filter-btn {
    padding: 0.35rem 0.65rem;
  }
}

.panel {
  position: absolute;
  z-index: 50;
  top: calc(100% + 6px);
  left: 0;
  width: 240px;
  max-width: calc(100vw - 2 * var(--page-gutter));
  max-height: 340px;
  overflow-y: auto;
  padding: 0.4rem;
  background: var(--color-surface-light);
  color: var(--color-text);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-1);
}

.option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.option:hover {
  background: var(--color-hover);
}

.option.dim {
  opacity: 0.45;
}

.option input {
  accent-color: var(--color-primary);
}

.option-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-count {
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
}

.empty {
  margin: 0;
  padding: 0.5rem;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.reset {
  width: 100%;
  margin-top: 0.3rem;
  padding: 0.45rem;
  background: none;
  border: none;
  border-top: 1px solid var(--color-divider);
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
}

.reset:hover {
  color: var(--color-text);
}
</style>
